import { FaCamera as CameraIcon } from 'react-icons/fa';
import { connect } from 'react-redux';
import FontAwesome from 'react-fontawesome';
import PropTypes from 'prop-types';
import React, { useMemo, useState, useEffect } from 'react';
import get from 'lodash/get';
import ReactAvatarEditor from 'react-avatar-editor';
import { Box } from 'rebass';

import { StyledAvatar, Initials, EditOverlay } from './styled';
import { selectEntity } from '../../entities/selectors';
import { imageUrlToBlob, renderPositionForVGBImage, scalePosition } from 'collections/watch-me-grow/components/ImageEditor/helper';

const getAvatarUrl = image => {
  const versionedAvatar = get(image, '["image-versions"][4].url', '');

  if (image.tmpUrl)
    return image.tmpUrl;

  if (versionedAvatar) {
    return versionedAvatar;
  }

  return image.tmpAvatar || image['asset-url'] || '';
};

const getInitials = name => {
  if (!name) return '';
  const splitName = name.split(' ');
  return splitName
    .map((word, index) => {
      if (index === 0 || index === splitName.length - 1) {
        return word[0];
      }
      return '';
    })
    .join('');
};

const Avatar = ({ image, value, tmpUrl, name, fallbackUrl, overflowCount, showEdit, ...props }) => {
  const avatarUrl = useMemo(() => tmpUrl || getAvatarUrl(image), [image, tmpUrl]);
  const initials = useMemo(() => getInitials(name), [name]);

  const [newImageObj, setNewImageObj] = useState(null);
  const [photoScaling, setPhotoScalling] = useState(null);
  const [scale, setScale] = useState(0);

  useEffect(() => {
    if ((value || image)) {
      if ((value || image)?.left !== null && (value || image)?.top !== null)
        setPhotoScalling({
          x: (value || image)?.left,
          y: (value || image)?.top,
        });
      setScale((value || image)?.zoom || 1);
    }
  }, [(value || image)]);

  const handleChangeImageUrl = async (url) => {
    if (url.startsWith('blob:') || url.startsWith('data:')) {
      setNewImageObj(url);
      return;
    }
    try {
      const blob = await imageUrlToBlob(url);
      setNewImageObj(URL.createObjectURL(blob));
    } catch (err) {
      setNewImageObj(url);
    }
  };

  useEffect(() => {
    if (avatarUrl) {
      handleChangeImageUrl(avatarUrl)
    }
  }, [avatarUrl, tmpUrl]);

  if (showEdit && (avatarUrl || fallbackUrl)) {
    return <Box width={40} height={40} sx={{ borderRadius: '50%' }}>
      <ReactAvatarEditor
        image={newImageObj}
        height={image?.height}
        width={image?.width}
        color={[250, 250, 250, 1]}
        scale={scale}
        id={`
          ${JSON.stringify(renderPositionForVGBImage(photoScaling), null, 2)}
          ${scale}
          `}
        position={renderPositionForVGBImage(photoScaling)}
        crossOrigin="anonymous"
        style={{
          minWidth: "100%",
          width: "100%",
          height: "100%",
          objectFit: 'cover',
          borderRadius: '50%'
          // zoom,
        }}
      />
    </Box>
  }

  if (avatarUrl || fallbackUrl) {
    const backgroundImage = `url(${avatarUrl || fallbackUrl})`;
    return (
      <StyledAvatar {...props} style={{ backgroundImage }}>
        {props.canEdit && (
          <EditOverlay>
            <CameraIcon size="30%" color="white" />
          </EditOverlay>
        )}
      </StyledAvatar>
    );
  }

  if (overflowCount) {
    return (
      <StyledAvatar {...props}>
        <Initials>{overflowCount}</Initials>
      </StyledAvatar>
    );
  }

  if (initials) {
    return (
      <StyledAvatar {...props}>
        {props.canEdit ? (
          <EditOverlay>
            <CameraIcon size="30%" color="white" />
          </EditOverlay>
        ) : (
          <Initials>{initials}</Initials>
        )}
      </StyledAvatar>
    );
  }

  return (
    <StyledAvatar {...props}>
      <Initials>
        <FontAwesome name="user" />
      </Initials>
    </StyledAvatar>
  );
};

const mapStateToProps = (state, { imageId }) => ({
  image: selectEntity({ state, type: 'images', id: imageId }),
});

Avatar.propTypes = {
  name: PropTypes.string,
  image: PropTypes.shape({}),
  border: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  size: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  fallbackUrl: PropTypes.string,
  canEdit: PropTypes.bool,
  overflowCount: PropTypes.string,
};

Avatar.defaultProps = {
  name: '',
  image: {},
  size: 36,
  border: false,
  fallbackUrl: '',
  canEdit: false,
  overflowCount: '',
};

export default connect(mapStateToProps)(Avatar);
